import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { SkillSummary, McpServerSummary } from '../types/team.js';
import * as teamApi from '../services/team-api.js';
import { navigateTo } from '../services/router.js';
import { wsClient } from '../services/websocket-client.js';
import '../components/shared/cp-page-subnav.js';

const SYSTEM_TABS = [
  { label: 'Settings', path: '/settings' },
  { label: 'Agenten', path: '/agents' },
  { label: 'Team & Skills', path: '/team' },
  { label: 'Agent-Skills', path: '/skills' },
  { label: 'Routinen', path: '/cron' },
  { label: 'Monitoring', path: '/monitoring' },
];

type ViewState = 'loading' | 'loaded' | 'empty' | 'error';
type McpLoadState = 'idle' | 'loading' | 'loaded' | 'error';

interface TeamGroup {
  name: string;
  skills: SkillSummary[];
}

@customElement('cp-page-team')
export class CpPageTeam extends LitElement {
  createRenderRoot() { return this; }

  @state() private _skills: SkillSummary[] = [];
  @state() private _viewState: ViewState = 'loading';
  @state() private _errorMessage = '';
  @state() private _detailModalOpen = false;
  @state() private _editModalOpen = false;
  @state() private _selectedSkillId = '';
  @state() private _confirmDialogOpen = false;
  @state() private _confirmDialogMessage = '';
  @state() private _deleteTargetSkillId = '';
  @state() private _terminalModalOpen = false;
  @state() private _mcpServers: McpServerSummary[] = [];
  @state() private _mcpLoadState: McpLoadState = 'idle';
  @state() private _mcpErrorMessage = '';
  @state() private _voiceConfigured = false;
  @state() private _voiceConfigLoaded = false;
  @state() private _voiceNotConfiguredDialogOpen = false;

  private _boundWsHandler = this._onWsMessage.bind(this);

  connectedCallback(): void {
    super.connectedCallback();
    this._loadSkills();
    this._loadMcpConfig();
    wsClient.addEventListener('ws-message', this._boundWsHandler as EventListener);
    this._requestVoiceConfig();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    wsClient.removeEventListener('ws-message', this._boundWsHandler as EventListener);
  }

  private _requestVoiceConfig(): void {
    wsClient.send({
      type: 'settings.voice.get',
      payload: {},
      timestamp: new Date().toISOString(),
    });
  }

  private _onWsMessage(e: Event): void {
    const detail = (e as CustomEvent<{ type: string; payload: unknown }>).detail;
    if (detail.type === 'settings.voice.status') {
      const payload = detail.payload as { deepgramConfigured: boolean; elevenLabsConfigured: boolean };
      this._voiceConfigured = payload.deepgramConfigured && payload.elevenLabsConfigured;
      this._voiceConfigLoaded = true;
    }
  }

  private async _loadSkills(): Promise<void> {
    this._viewState = 'loading';
    try {
      this._skills = await teamApi.getSkills();
      this._viewState = this._skills.length > 0 ? 'loaded' : 'empty';
    } catch (e) {
      this._errorMessage = e instanceof Error ? e.message : 'Fehler beim Laden der Skills';
      this._viewState = 'error';
    }
  }

  private async _loadMcpConfig(): Promise<void> {
    this._mcpLoadState = 'loading';
    try {
      const response = await teamApi.getMcpConfig();
      this._mcpServers = response.servers || [];
      this._mcpLoadState = 'loaded';
    } catch (e) {
      this._mcpErrorMessage = e instanceof Error ? e.message : 'Fehler beim Laden der MCP-Konfiguration';
      this._mcpLoadState = 'error';
    }
  }

  private _getDevTeamSkills(): SkillSummary[] {
    return this._skills.filter(s => !s.teamType || s.teamType === 'devteam');
  }

  private _getCustomTeamGroups(): TeamGroup[] {
    const teamSkills = this._skills.filter(s => s.teamType === 'team');
    const groups = new Map<string, SkillSummary[]>();
    for (const skill of teamSkills) {
      const name = skill.teamName || 'Unbenanntes Team';
      const list = groups.get(name) || [];
      list.push(skill);
      groups.set(name, list);
    }
    return Array.from(groups.entries())
      .map(([name, skills]) => ({ name, skills }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private _getIndividualSkills(): SkillSummary[] {
    return this._skills.filter(s => s.teamType === 'individual');
  }

  private get _availableMcpToolNames(): string[] {
    return this._mcpLoadState === 'loaded' ? this._mcpServers.map(s => s.name) : [];
  }

  private _handleCardClick(e: CustomEvent<{ skillId: string }>): void {
    this._selectedSkillId = e.detail.skillId;
    this._detailModalOpen = true;
  }

  private _handleCallClick(e: CustomEvent<{ skillId: string }>): void {
    if (!this._voiceConfigLoaded || !this._voiceConfigured) {
      this._voiceNotConfiguredDialogOpen = true;
      return;
    }
    navigateTo(`/call/${e.detail.skillId}`);
  }

  private _handleVoiceNotConfiguredConfirm(): void {
    this._voiceNotConfiguredDialogOpen = false;
    navigateTo('/settings');
  }

  private _handleVoiceNotConfiguredCancel(): void {
    this._voiceNotConfiguredDialogOpen = false;
  }

  private _handleEditClick(e: CustomEvent<{ skillId: string }>): void {
    this._selectedSkillId = e.detail.skillId;
    this._detailModalOpen = false;
    this._editModalOpen = true;
  }

  private _handleDeleteClick(e: CustomEvent<{ skillId: string; skillName: string; teamType?: string }>): void {
    const { skillId, skillName, teamType } = e.detail;
    this._deleteTargetSkillId = skillId;
    const isDevTeam = !teamType || teamType === 'devteam';
    this._confirmDialogMessage = isDevTeam
      ? `Dieser Skill gehoert zum Development Team. "${skillName}" wirklich loeschen?`
      : `"${skillName}" wirklich loeschen?`;
    this._detailModalOpen = false;
    this._confirmDialogOpen = true;
  }

  private _handleDetailModalClose(): void {
    this._detailModalOpen = false;
  }

  private _handleEditModalClose(): void {
    this._editModalOpen = false;
  }

  private _handleSkillSaved(): void {
    this._editModalOpen = false;
    this._loadSkills();
  }

  private async _handleDeleteConfirm(): Promise<void> {
    this._confirmDialogOpen = false;
    if (!this._deleteTargetSkillId) return;

    try {
      await teamApi.deleteSkill(this._deleteTargetSkillId);
      this._deleteTargetSkillId = '';
      this._loadSkills();
    } catch (e) {
      console.error('Error deleting skill:', e);
      this._deleteTargetSkillId = '';
    }
  }

  private _handleDeleteCancel(): void {
    this._confirmDialogOpen = false;
    this._deleteTargetSkillId = '';
  }

  private _openAddTeamMember(): void {
    this._terminalModalOpen = true;
  }

  private _handleTerminalClosed(e: CustomEvent<{ completed: boolean }>): void {
    this._terminalModalOpen = false;
    if (e.detail.completed) {
      this._loadSkills();
    }
  }

  render() {
    return html`
      <div class="cp-page">
        <cp-page-subnav .tabs=${SYSTEM_TABS} activePath="/team"></cp-page-subnav>
        <div class="cp-page-team__header">
          <div class="cp-page-team__header-row">
            <h1 class="cp-page__title">Team</h1>
            <button class="cp-page-team__add-btn" @click=${this._openAddTeamMember}>+ Teammitglied hinzufuegen</button>
          </div>
          <p class="cp-page-team__subtitle">Skills und Agents in deinem Projekt</p>
        </div>
        ${this._renderContent()}
      </div>
      <cp-team-detail-modal
        .open=${this._detailModalOpen}
        .skillId=${this._selectedSkillId}
        .availableMcpTools=${this._availableMcpToolNames}
        @modal-closed=${this._handleDetailModalClose}
        @edit-click=${this._handleEditClick}
        @delete-click=${this._handleDeleteClick}
      ></cp-team-detail-modal>
      <cp-team-edit-modal
        .open=${this._editModalOpen}
        .skillId=${this._selectedSkillId}
        .availableMcpTools=${this._availableMcpToolNames}
        @modal-closed=${this._handleEditModalClose}
        @skill-saved=${this._handleSkillSaved}
      ></cp-team-edit-modal>
      <cp-confirm-dialog
        .open=${this._confirmDialogOpen}
        title="Skill loeschen"
        .message=${this._confirmDialogMessage}
        confirmLabel="Loeschen"
        variant="danger"
        @dialog-confirmed=${this._handleDeleteConfirm}
        @dialog-cancelled=${this._handleDeleteCancel}
      ></cp-confirm-dialog>
      <cp-team-terminal-modal
        .open=${this._terminalModalOpen}
        @terminal-closed=${this._handleTerminalClosed}
      ></cp-team-terminal-modal>
      <cp-confirm-dialog
        .open=${this._voiceNotConfiguredDialogOpen}
        title="Voice nicht konfiguriert"
        message="Die Voice-API-Keys sind nicht konfiguriert. Bitte konfiguriere Deepgram und ElevenLabs in den Settings."
        confirmLabel="Zu den Settings"
        @dialog-confirmed=${this._handleVoiceNotConfiguredConfirm}
        @dialog-cancelled=${this._handleVoiceNotConfiguredCancel}
      ></cp-confirm-dialog>
    `;
  }

  private _renderContent() {
    switch (this._viewState) {
      case 'loading': return this._renderLoading();
      case 'error': return this._renderError();
      case 'empty': return this._renderEmpty();
      case 'loaded': return this._renderGrouped();
    }
  }

  private _renderLoading() {
    return html`
      <div class="cp-page-team__grid">
        ${[1, 2, 3, 4].map(() => html`
          <div class="cp-page-team__skeleton">
            <div class="cp-page-team__skeleton-line cp-page-team__skeleton-line--title"></div>
            <div class="cp-page-team__skeleton-line"></div>
            <div class="cp-page-team__skeleton-line cp-page-team__skeleton-line--short"></div>
          </div>
        `)}
      </div>
    `;
  }

  private _renderError() {
    return html`
      <div class="cp-page-team__empty">
        <div class="cp-page-team__empty-icon">&#9888;</div>
        <h3 class="cp-page-team__empty-title">Fehler beim Laden</h3>
        <p class="cp-page-team__empty-text">${this._errorMessage}</p>
        <button class="cp-page-team__retry-btn" @click=${() => this._loadSkills()}>Erneut versuchen</button>
      </div>
    `;
  }

  private _renderEmpty() {
    return html`
      <div class="cp-page-team__empty">
        <div class="cp-page-team__empty-icon" style="font-size: 2rem;">&#9824;</div>
        <h3 class="cp-page-team__empty-title">Noch kein Team vorhanden</h3>
        <p class="cp-page-team__empty-text">
          Erstelle dein Development Team mit spezialisierten Skills und Agents.
        </p>
        <button class="cp-page-team__retry-btn" @click=${this._openAddTeamMember}>Teammitglied hinzufuegen</button>
      </div>
    `;
  }

  private _renderGrouped() {
    const devTeam = this._getDevTeamSkills();
    const customTeams = this._getCustomTeamGroups();
    const individuals = this._getIndividualSkills();

    return html`
      ${devTeam.length > 0 ? this._renderSection('Development Team', devTeam) : nothing}
      ${customTeams.length > 0 ? html`
        <div class="cp-page-team__section">
          <h2 class="cp-page-team__section-title">Custom Teams</h2>
          ${customTeams.map(group => html`
            <div class="cp-page-team__group">
              <h3 class="cp-page-team__group-name">${group.name}</h3>
              <div class="cp-page-team__grid">
                ${group.skills.map(skill => html`
                  <cp-team-card
                    .skill=${skill}
                    .availableMcpTools=${this._availableMcpToolNames}
                    @card-click=${this._handleCardClick}
                    @edit-click=${this._handleEditClick}
                    @delete-click=${this._handleDeleteClick}
                    @call-click=${this._handleCallClick}
                  ></cp-team-card>
                `)}
              </div>
            </div>
          `)}
        </div>
      ` : nothing}
      ${individuals.length > 0 ? this._renderSection('Einzelpersonen', individuals) : nothing}
      ${this._renderMcpSection()}
    `;
  }

  private _renderMcpSection() {
    if (this._mcpLoadState === 'idle' || this._mcpLoadState === 'loading') {
      return nothing;
    }

    if (this._mcpLoadState === 'error') {
      return html`
        <div class="cp-page-team__section">
          <h2 class="cp-page-team__section-title">MCP Tools</h2>
          <div class="mcp-section__message mcp-section__message--error">
            ${this._mcpErrorMessage}
          </div>
        </div>
      `;
    }

    if (this._mcpServers.length === 0) {
      return html`
        <div class="cp-page-team__section">
          <h2 class="cp-page-team__section-title">MCP Tools</h2>
          <div class="mcp-section__message">
            Keine MCP-Server konfiguriert
          </div>
        </div>
      `;
    }

    return html`
      <div class="cp-page-team__section">
        <h2 class="cp-page-team__section-title">MCP Tools</h2>
        <div class="cp-page-team__grid">
          ${this._mcpServers.map(server => html`
            <cp-mcp-server-card .server=${server}></cp-mcp-server-card>
          `)}
        </div>
      </div>
    `;
  }

  private _renderSection(title: string, skills: SkillSummary[]) {
    return html`
      <div class="cp-page-team__section">
        <h2 class="cp-page-team__section-title">${title}</h2>
        <div class="cp-page-team__grid">
          ${skills.map(skill => html`
            <cp-team-card
              .skill=${skill}
              .availableMcpTools=${this._availableMcpToolNames}
              @card-click=${this._handleCardClick}
              @edit-click=${this._handleEditClick}
              @delete-click=${this._handleDeleteClick}
              @call-click=${this._handleCallClick}
            ></cp-team-card>
          `)}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-page-team': CpPageTeam;
  }
}
